import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { SingleValue } from 'react-select';
import { HStack, StackProps } from '@chakra-ui/react';
import {
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subMonths,
  subQuarters,
  subYears,
} from 'date-fns';

import * as Form from 'components/_core/form';
import {
  DatePickerProps,
  DatePickerViewMode,
} from 'components/_core/SunshineDatePicker';
import Text from 'components/_core/Text';
import { LLLL_yyyy, QQQQ, yyyy } from 'helpers/date';

import locales from './locales';
import { getDefaultSelectedDate, Option, RangeIntervalDate } from './utils';

type RangeIntervalPickerProps = Omit<
  StackProps,
  'children' | 'onChange' | 'defaultValue'
> & {
  onChange: (value: RangeIntervalDate) => void;
  value: RangeIntervalDate;
  periodOptions?: Periods[];
};

const PERIOD_OPTIONS = [
  {
    label: locales.periods.monthly,
    value: 'month',
  },
  {
    label: locales.periods.quarterly,
    value: 'quarter',
  },
  {
    label: locales.periods.yearly,
    value: 'year',
  },
  {
    label: locales.periods.custom,
    value: 'custom',
  },
] as const;

type Periods = (typeof PERIOD_OPTIONS)[number]['value'];

const modifiers = {
  disabled: { after: new Date() },
} satisfies DatePickerProps['modifiers'];

const RangeIntervalPicker: FC<RangeIntervalPickerProps> = ({
  onChange,
  periodOptions = PERIOD_OPTIONS.map(({ value }) => value),
  value,
  ...props
}) => {
  const filteredPeriodOptions = PERIOD_OPTIONS.filter(
    (item) => periodOptions?.includes(item.value),
  );
  const initialPeriod = value?.type
    ? filteredPeriodOptions.find(
        ({ value: optionValue }) => optionValue === value.type,
      )
    : null;

  const [period, setPeriod] = useState<SingleValue<Option<Periods>>>(
    // @ts-expect-error filteredPeriodOptions[0] is possibly 'undefined'.
    initialPeriod || filteredPeriodOptions[0],
  );
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    value.currentInterval?.from ||
      getDefaultSelectedDate(
        // @ts-expect-error Object is possibly 'undefined'.
        filteredPeriodOptions[0].value as DatePickerViewMode,
      ),
  );
  const previousValues = useRef<{
    date: Date | undefined;
    period: SingleValue<Option<Periods>>;
  }>({
    date: selectedDate,
    period,
  });

  const hasCustomPeriod = period?.value === 'custom';

  useEffect(() => {
    if (!selectedDate) {
      return;
    }
    if (
      previousValues.current.period?.value === period?.value &&
      previousValues.current.date &&
      startOfDay(selectedDate).getTime() ===
        startOfDay(previousValues.current.date).getTime()
    ) {
      return;
    }

    if (period?.value === 'month') {
      const lastMonth = subMonths(selectedDate, 1);

      onChange({
        currentInterval: {
          from: startOfMonth(selectedDate),
          to: endOfMonth(selectedDate),
        },
        previousInterval: {
          from: startOfMonth(lastMonth),
          to: endOfMonth(lastMonth),
        },
        type: 'month',
      });
    } else if (period?.value === 'quarter') {
      const lastQuarter = subQuarters(selectedDate, 1);

      onChange({
        currentInterval: {
          from: startOfQuarter(selectedDate),
          to: endOfQuarter(selectedDate),
        },
        previousInterval: {
          from: startOfQuarter(lastQuarter),
          to: endOfQuarter(lastQuarter),
        },
        type: 'quarter',
      });
    } else if (period?.value === 'year') {
      const lastYear = subYears(selectedDate, 1);

      onChange({
        currentInterval: {
          from: startOfYear(selectedDate),
          to: endOfYear(selectedDate),
        },
        previousInterval: {
          from: startOfYear(lastYear),
          to: endOfYear(lastYear),
        },
        type: 'year',
      });
    }
    previousValues.current.date = selectedDate;
  }, [onChange, selectedDate, period]);

  return (
    <HStack alignItems="center" {...props}>
      <Form.Field minWidth={256}>
        {filteredPeriodOptions.length > 1 ? (
          <Form.SunshineSelect
            onChange={(newPeriod) => {
              previousValues.current.period = period;
              setPeriod(newPeriod);
              if (newPeriod?.value) {
                if (newPeriod.value === 'custom') {
                  const today = new Date();
                  onChange({
                    currentInterval: {
                      from: startOfMonth(today),
                      to: today,
                    },
                    previousInterval: null,
                    type: 'custom',
                  });
                } else {
                  setSelectedDate(getDefaultSelectedDate(newPeriod.value));
                }
              }
            }}
            options={filteredPeriodOptions}
            value={period}
          />
        ) : (
          /**
           * Specific behavior for Treasury graph having only Year choice for now.
           * This can be removed later if we put back multiple choices for all graphs.
           * @see https://linear.app/shine/issue/CPA-1246/quarter-view-of-the-tresorerie-graph-is-not-readable
           */
          <Form.Input isReadOnly={true} value={initialPeriod?.label} />
        )}
      </Form.Field>
      {!hasCustomPeriod ? (
        <Form.Field minWidth={180}>
          <Form.SunshineInputDatePicker
            dateFormat={(() => {
              if (period?.value === 'month') {
                return LLLL_yyyy;
              }
              if (period?.value === 'quarter') {
                return QQQQ;
              }
              if (period?.value === 'year') {
                return yyyy;
              }

              return LLLL_yyyy;
            })()}
            inputProps={{
              rightIcon: 'chevron-down',
            }}
            modifiers={modifiers}
            onChange={setSelectedDate}
            value={selectedDate}
            viewMode={(period?.value ?? 'month') as DatePickerViewMode}
          />
        </Form.Field>
      ) : (
        <HStack spacing="space-8">
          <Text size="sm">{locales.from}</Text>
          <Form.Field>
            <Form.SunshineInputDatePicker
              modifiers={modifiers}
              onChange={(newDate) => {
                if (newDate) {
                  onChange({
                    currentInterval: {
                      from: startOfDay(newDate),
                      to: value.currentInterval.to,
                    },
                    previousInterval: null,
                    type: period.value,
                  });
                }
              }}
              value={value.currentInterval.from}
            />
          </Form.Field>
          <Text size="sm">{locales.to}</Text>
          <Form.Field>
            <Form.SunshineInputDatePicker
              modifiers={modifiers}
              onChange={(newDate) => {
                if (newDate) {
                  onChange({
                    currentInterval: {
                      from: value.currentInterval.from,
                      to: endOfDay(newDate),
                    },
                    previousInterval: null,
                    type: period.value,
                  });
                }
              }}
              value={value.currentInterval.to}
            />
          </Form.Field>
        </HStack>
      )}
    </HStack>
  );
};

export default RangeIntervalPicker;
